// The gantt body's boundary (TODO §26/§3.2, §4.3): seeding, the ISO⇄Date conversion that
// keeps SVAR's model out of Redux, the timeline scales, and the fit maths that turns a
// scrolling widget into something that sits still on a slide.
//
// Deliberately free of any runtime import from `@svar-ui/*`: `ContentWindow` calls
// `createGanttDoc` on the "Add gantt" click, and a value import here would drag the library
// into the main bundle for every deck that never uses one (§26/§7). The two type-only
// imports below are erased at build time.

import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarQuarters,
  differenceInCalendarWeeks,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
} from "date-fns";

import {
  isNeutralColor,
  ptToPx,
  resolveRoleTypography,
  sheetSwatches,
  swatchValue,
} from "@/lib/stylesheet-typography";
import type { GriddStyleSheetData } from "@/types/document";
import type {
  GanttDocument,
  GanttId,
  GanttLink,
  GanttTask,
  GanttView,
} from "@/types/gantt";

// One row of the timeline header. Structurally SVAR's `IScaleConfig`, written out here so
// this file owes the package nothing at runtime.
export interface GanttScale {
  unit: GanttView["unit"] | "month" | "year";
  step: number;
  format: string;
}

// Everything the component has to hand `<Gantt>` so the whole schedule lands inside the
// content window with no scrollbars. Derived, never stored — the window's size changes on
// every split drag, and a stored copy would be stale by the next frame.
export interface GanttFit {
  start: Date;
  end: Date;
  cellWidth: number;
  cellHeight: number;
  scaleHeight: number;
  // §26/§4.5 Passed EXPLICITLY. Left to itself the widget derives the grid's width from its
  // own DEFAULT columns, not from the ones we hand it, so the task list came out far wider
  // than the fit had reserved and pushed the timeline off the edge of the window.
  gridWidth: number;
  scales: GanttScale[];
  lengthUnit: GanttView["unit"];
  // Rows actually drawn, so the caller can size the widget to its CONTENT rather than
  // stretching it down the window (§4.6).
  rows: number;
  // The type size every other number here was derived from, in the same units.
  fontPx: number;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
// The task fields SVAR hands back as `Date` objects. Everything else round-trips as-is.
const DATE_KEYS = ["start", "end", "base_start", "base_end"] as const;

// === ISO ⇄ Date, the one boundary (§3.2.2) =================================================

// `YYYY-MM-DD` rather than a full ISO timestamp: a schedule is day-grained, and a timestamp
// would make the stored value depend on the author's timezone — reopening a deck in another
// zone would shift every bar by a day. Parsed back as LOCAL midnight for the same reason
// (`new Date("2026-05-04")` is UTC midnight, which is the previous day west of Greenwich).
export function toIsoDay(value: Date): string {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function fromIsoDay(value: string): Date | undefined {
  const match = ISO_DATE.exec(value);
  if (!match) return undefined;
  const [y, m, d] = match[0].split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Stored shape → what `<Gantt tasks={…}>` wants. Unknown keys pass through untouched, so a
// field the built-in editor writes and we have never heard of still reaches the widget.
export function reviveTasks(tasks: GanttTask[]): Record<string, unknown>[] {
  return tasks.map((task) => {
    const out: Record<string, unknown> = { ...task };
    for (const key of DATE_KEYS) {
      const raw = out[key];
      if (typeof raw === "string") {
        const date = fromIsoDay(raw);
        if (date) out[key] = date;
        else delete out[key];
      }
    }
    return out;
  });
}

// `api.serialize()` → the stored shape. Two things are dropped deliberately:
//
//   • `data` — the library's serializer flattens its tree but leaves each node's child array
//     attached, so persisting it would store every task twice and let the two copies drift.
//     Hierarchy is carried by `parent`, which is also the only form SVAR accepts on the way
//     back in.
//   • `$`-prefixed keys — `$level`, `$wbs`, `$x`, `$w` and friends are computed geometry,
//     recomputed on every render and meaningless in a file.
//
// Everything else is KEPT, including keys we do not know about: a pick-list would silently
// drop whatever the editor or a context-menu action writes next, and that failure is
// invisible until someone reopens the deck.
export function serializeTasks(raw: unknown[]): GanttTask[] {
  const out: GanttTask[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const source = item as Record<string, unknown>;
    if (source.id === undefined || source.id === null) continue;
    const task: GanttTask = { id: source.id as GanttTask["id"] };
    for (const [key, value] of Object.entries(source)) {
      if (key === "data" || key.startsWith("$")) continue;
      if (value === undefined) continue;
      task[key] = value instanceof Date ? toIsoDay(value) : value;
    }
    out.push(task);
  }
  return out;
}

export function serializeLinks(raw: unknown[]): GanttLink[] {
  const out: GanttLink[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const link = item as Record<string, unknown>;
    if (link.id === undefined || link.source === undefined) continue;
    out.push({
      id: link.id as GanttLink["id"],
      source: link.source as GanttLink["source"],
      target: link.target as GanttLink["target"],
      type: (link.type as GanttLink["type"]) ?? "e2s",
      ...(typeof link.lag === "number" ? { lag: link.lag } : {}),
    });
  }
  return out;
}

// === The timeline ==========================================================================

export function ganttScales(unit: GanttView["unit"]): GanttScale[] {
  // **These are strftime-style `%` flags, NOT date-fns patterns**, and the failure is silent:
  // SVAR compiles a string format with `dateToString`, which replaces `/%[a-zA-Z]/g` and
  // returns everything else verbatim — so `"MMM yyyy"` renders as the literal text "MMM yyyy"
  // in every header cell. The flags that matter here: `%M` short month, `%F` full month,
  // `%Y` year, `%j` day of month, `%w` week number, `%Q` quarter.
  switch (unit) {
    case "day":
      return [
        { unit: "month", step: 1, format: "%F %Y" },
        { unit: "day", step: 1, format: "%j" },
      ];
    case "week":
      return [
        { unit: "month", step: 1, format: "%M %Y" },
        { unit: "week", step: 1, format: "W%w" },
      ];
    case "month":
      return [
        { unit: "year", step: 1, format: "%Y" },
        { unit: "month", step: 1, format: "%M" },
      ];
    case "quarter":
      return [
        { unit: "year", step: 1, format: "%Y" },
        { unit: "quarter", step: 1, format: "Q%Q" },
      ];
  }
}

// §26/§4.10 The day a week starts on, as the WIDGET understands it — never assumed. SVAR's
// scale builder aligns the range down to a week boundary and extends the end to cover, so if
// our alignment and its alignment disagree by a day, it draws one MORE column than the fit
// measured and the chart overflows the window by exactly one cell.
//
// And they did disagree: its English locale ships `weekStart: 0` (Sunday) while a project week
// is conventionally Monday. `GanttCanvas` reads the real value out of `api.getState()`
// (§5.6) and passes it here, so the two are the same number by construction rather than by
// hope — including if a locale, a default or a package version ever changes it.
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6;

function unitStart(
  date: Date,
  unit: GanttView["unit"],
  weekStart: WeekStart,
): Date {
  switch (unit) {
    case "day":
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    case "week":
      return startOfWeek(date, { weekStartsOn: weekStart });
    case "month":
      return startOfMonth(date);
    case "quarter":
      return startOfQuarter(date);
  }
}

function addUnits(date: Date, unit: GanttView["unit"], count: number): Date {
  switch (unit) {
    case "day":
      return addDays(date, count);
    case "week":
      return addWeeks(date, count);
    case "month":
      return addMonths(date, count);
    case "quarter":
      return addQuarters(date, count);
  }
}

function unitsBetween(
  a: Date,
  b: Date,
  unit: GanttView["unit"],
  weekStart: WeekStart,
): number {
  switch (unit) {
    case "day":
      return differenceInCalendarDays(b, a);
    case "week":
      return differenceInCalendarWeeks(b, a, { weekStartsOn: weekStart });
    case "month":
      return differenceInCalendarMonths(b, a);
    case "quarter":
      return differenceInCalendarQuarters(b, a);
  }
}

// The schedule's own extent, as dates. Returns null for an empty schedule — the caller then
// shows a window around today rather than a zero-width timeline.
export function ganttBounds(
  tasks: GanttTask[],
): { start: Date; end: Date } | null {
  let min: Date | null = null;
  let max: Date | null = null;
  for (const task of tasks) {
    const start = task.start ? fromIsoDay(task.start) : undefined;
    const end = task.end ? fromIsoDay(task.end) : start;
    if (start && (!min || start < min)) min = start;
    if (end && (!max || end > max)) max = end;
  }
  return min && max ? { start: min, end: max } : null;
}

// How many rows the chart will draw: every task except those inside a collapsed branch.
// Sizing off `tasks.length` instead makes a deck with a closed summary leave a stripe of
// empty chart under the bars.
//
// Note the default: SVAR treats `open` as "open at initialization", so an ABSENT `open` is a
// CLOSED branch — which is why the seed sets it explicitly and why the test below is
// `open === true` rather than `open !== false`.
function visibleRowCount(tasks: GanttTask[]): number {
  const hiddenParents = new Set<GanttTask["id"]>();
  let rows = 0;
  for (const task of tasks) {
    const hidden = task.parent !== undefined && hiddenParents.has(task.parent);
    // A hidden task's children are hidden too, and so is a visible-but-closed one's.
    if (hidden || task.open !== true) hiddenParents.add(task.id);
    if (hidden) continue;
    rows += 1;
  }
  return rows;
}

// §26/§4.4 The type size the whole chart is built from — the deck's own **SmallBody** preset
// (Gridd's factory sheet: 10pt against Body's 12pt), falling back to a derived one.
//
// This replaced a size derived from the row height, which had it backwards: it let the window
// decide how big the text was, so a six-task schedule in a tall window drew 24px type and read
// as a chart someone had zoomed into. A gantt is secondary text on a slide — the same size as
// the deck's other small text, whatever that is — and the ROWS follow from the type, not the
// other way round.
//
// In the slide's LOGICAL units, like everything else here: the chart is laid out on the
// 1600×900 canvas and scaled by the canvas transform, so no view-dependent factor enters.
export function ganttFontPx(styleSheet: GriddStyleSheetData): number {
  const small = resolveRoleTypography(styleSheet, "smallBody");
  return Math.max(6, ptToPx(small.fontSizePt));
}

// §4.3 Fit the schedule into the window. A slide must never contain a scrollbox: the author
// cannot scroll it in a PDF and the audience cannot scroll it in presentation, so a chart that
// overflows is a chart with content nobody can see.
//
// `scaleHeight` is PER SCALE ROW in SVAR (`g = scaleHeight * rows` in its scale builder), so
// the header costs `scaleHeight × scales.length` and the fit must reserve exactly that.
export function fitGantt(
  doc: GanttDocument,
  box: { width: number; height: number },
  fontPx: number,
  weekStart: WeekStart,
): GanttFit {
  const { view } = doc;
  const scales = ganttScales(view.unit);
  const bounds = ganttBounds(doc.tasks);
  const today = new Date();
  // One unit of air on each side, so the first bar does not start flush against the frame.
  const rawStart = bounds?.start ?? today;
  const rawEnd = bounds?.end ?? addUnits(today, view.unit, 6);
  // §4.12 An authored render window wins over the derived one, and takes no padding: it is an
  // exact request. A start on or after its end is ignored ENTIRELY rather than half-applied —
  // it would otherwise draw a zero-column chart with nothing on screen to say why — and each
  // end falls back independently, so setting only one is meaningful.
  const authoredStart = view.rangeStart
    ? fromIsoDay(view.rangeStart)
    : undefined;
  const authoredEnd = view.rangeEnd ? fromIsoDay(view.rangeEnd) : undefined;
  const usable = !authoredStart || !authoredEnd || authoredStart < authoredEnd;

  // Both ends are aligned with the widget's own boundary, so its `alignDown(start)` and
  // `extendToCover(end)` are no-ops and the column count it draws is the one measured here.
  // The authored end is inclusive — asking for "to 30 November" must DRAW November — so it
  // gets one unit added where the derived end gets two units of air.
  const start =
    usable && authoredStart
      ? unitStart(authoredStart, view.unit, weekStart)
      : addUnits(unitStart(rawStart, view.unit, weekStart), view.unit, -1);
  const end =
    usable && authoredEnd
      ? addUnits(unitStart(authoredEnd, view.unit, weekStart), view.unit, 1)
      : addUnits(unitStart(rawEnd, view.unit, weekStart), view.unit, 2);

  const rows = Math.max(1, visibleRowCount(doc.tasks));
  const cells = Math.max(1, unitsBetween(start, end, view.unit, weekStart));
  const gridWidth = view.showTaskList ? taskListWidth(box.width, fontPx) : 0;
  // §4.6 Rows are a multiple of the type, bounded by what the window can show. A row is 2.2×
  // the type where there is room — comfortable for a bar with a label inside it — and shrinks
  // toward 1.5× before the chart starts clipping.
  const roomPerRow = (box.height - fontPx * 3.6) / rows;
  const cellHeight = Math.round(
    Math.max(fontPx * 1.5, Math.min(fontPx * 2.2, roomPerRow)),
  );
  const scaleHeight = Math.round(fontPx * 1.7);

  if (view.fit === "fixed") {
    return {
      start,
      end,
      cellWidth: view.cellWidth,
      cellHeight: view.cellHeight,
      scaleHeight,
      gridWidth,
      scales,
      lengthUnit: view.unit,
      rows,
      fontPx,
    };
  }

  // The floor is what stops the fit from degenerating: a three-year day-scale schedule wants a
  // sub-pixel column. Below it the chart clips — the honest failure, and the cue to widen the
  // unit.
  const cellWidth = Math.max(
    Math.round(fontPx * 0.9),
    Math.floor((box.width - gridWidth - 2) / cells),
  );
  return {
    start,
    end,
    cellWidth,
    cellHeight,
    scaleHeight,
    gridWidth,
    scales,
    lengthUnit: view.unit,
    rows,
    fontPx,
  };
}

// The left-hand task grid: wide enough for a real task name at the deck's own type size, and
// never more than a third of the window. Measured in ems rather than px because the type size
// is what changes between the editing and at-rest copies (§5.2) — a px constant would be right
// in one of them and wrong in the other.
export function taskListWidth(boxWidth: number, fontPx: number): number {
  return Math.round(
    Math.max(fontPx * 8, Math.min(boxWidth * 0.32, fontPx * 16)),
  );
}

// === The seed ==============================================================================

export const DEFAULT_GANTT_VIEW: GanttView = {
  unit: "week",
  fit: "window",
  cellWidth: 64,
  cellHeight: 34,
  showTaskList: true,
  showProgress: true,
  showLinks: true,
};

// A new gantt is a small, plausible plan rather than an empty grid — the same call the grid
// seed makes (§7.2): an empty widget on a slide reads as broken, and the first thing an
// author does is rename what is already there.
//
// Anchored on the Monday of the current week, so a deck made today opens on a timeline that
// starts today rather than in 2024.
export function createGanttDoc(): GanttDocument {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  const day = (offset: number) => toIsoDay(addDays(monday, offset));
  const ids = {
    discovery: crypto.randomUUID(),
    interviews: crypto.randomUUID(),
    review: crypto.randomUUID(),
    design: crypto.randomUUID(),
    build: crypto.randomUUID(),
    live: crypto.randomUUID(),
  };
  const tasks: GanttTask[] = [
    {
      id: ids.discovery,
      text: "Discovery",
      type: "summary",
      start: day(0),
      end: day(21),
      open: true,
    },
    {
      id: ids.interviews,
      text: "Stakeholder interviews",
      type: "task",
      parent: ids.discovery,
      start: day(0),
      end: day(14),
      progress: 60,
    },
    {
      id: ids.review,
      text: "Data review",
      type: "task",
      parent: ids.discovery,
      start: day(7),
      end: day(21),
      progress: 20,
    },
    {
      id: ids.design,
      text: "Design",
      type: "task",
      start: day(21),
      end: day(42),
      progress: 0,
    },
    {
      id: ids.build,
      text: "Build",
      type: "task",
      start: day(42),
      end: day(70),
      progress: 0,
    },
    {
      id: ids.live,
      text: "Go live",
      type: "milestone",
      start: day(70),
      end: day(70),
    },
  ];
  const links: GanttLink[] = [
    {
      id: crypto.randomUUID(),
      source: ids.discovery,
      target: ids.design,
      type: "e2s",
    },
    {
      id: crypto.randomUUID(),
      source: ids.design,
      target: ids.build,
      type: "e2s",
    },
    {
      id: crypto.randomUUID(),
      source: ids.build,
      target: ids.live,
      type: "e2s",
    },
  ];
  return { version: 1, engine: "svar", tasks, links, view: DEFAULT_GANTT_VIEW };
}

// === The deck's look, as far as it goes (§26/§6) ===========================================
//
// A gantt takes its font and ONE accent from the deck stylesheet and stops there. The same
// call §21/§9.1 made for diagrams, for the same reason: the library owns a complete visual
// system of its own, and a half-mapping of it (bars restyled, popups and form controls not)
// looks worse than an honest untouched one. The sheet's `dataVisualizationColors` field stays
// reserved for §15's chart palette.
//
// The accent is a NAME lookup first, so a sheet swap recolours every gantt in the deck for
// free — the §7.4 seam, exactly as the sticker tone uses it.
export interface GanttTheme {
  fontFamily?: string;
  fontColor: string;
  // The bar body.
  accent: string;
  // The PROGRESS portion, drawn over the bar — so it has to be the STRONGER of the two.
  // The package's own Willow palette pairs #3983eb with #1f6bd9 in exactly that direction,
  // and reversing it reads as "the unfinished part is the solid one", which is backwards.
  accentStrong: string;
  // Summary (parent) bars and their progress. Willow ships TEAL for these and PURPLE for
  // milestones — two hues nobody chose, on a deck whose palette is the whole point. They are
  // derived from the deck's ink instead, so a summary reads as structure rather than as a
  // third data series.
  summary: string;
  summaryStrong: string;
  lineColor: string;
  // Grid rules and the frame: a hairline, not the app's 1px slate.
  borderColor: string;
  selectColor: string;
}

// Matched as PREFIXES, not exact names: Gridd's factory sheet calls its accent `accent1`, and
// an exact list quietly fell through to the next rule — which picked `text` (#333333) and drew
// every bar in near-black. Order is priority.
const ACCENT_NAMES = ["accent", "primary", "brand", "highlight"];
// Near-white, near-black and greys make a chart nobody can read, so they are skipped when
// falling back to "the first colour in the sheet". Tested by SATURATION rather than by a
// pattern of repeated characters: the first version only caught colours whose channels were
// literally identical, so `#333333` passed as colourful and became the accent.
// §15 Both of these moved to `stylesheet-typography.ts` when charts needed the same
// judgement, and are re-exported here so every existing `from "@/lib/gantt"` import keeps
// working. The move is not cosmetic: importing them from this module would pull `date-fns`
// into the chart bundle for a `find` and a hex test.
export { swatchValue };

// The "completed" half of a pair, when only the base is chosen: the same colour carried
// toward black, which is the relationship the package's own palette uses (#3983eb over
// #1f6bd9) and the only one that reads as "this part is done".
export function doneFromBase(base: string): string {
  return `color-mix(in srgb, ${base} 72%, black)`;
}

// §26/§6.3 The chart's effective pair, before any per-task override: the view's chosen names
// if it has them, otherwise the deck accent from `resolveGanttTheme`.
export function resolveChartColors(
  view: GanttView,
  theme: GanttTheme,
  swatches: { name: string; value: string }[],
): {
  base: string;
  done: string;
  summary: string;
  summaryDone: string;
  milestone: string;
} {
  const base = swatchValue(swatches, view.baseColorName) ?? theme.accent;
  const done =
    swatchValue(swatches, view.doneColorName) ??
    (view.baseColorName ? doneFromBase(base) : theme.accentStrong);
  // §6.3 Summary and milestone are their own choices, because they are their own ROLES: a
  // summary is structure and a milestone is an event, and tying either to the task colour
  // makes a chart where the eye cannot separate the three. Unset, they keep the deck-derived
  // neutrals from `resolveGanttTheme` rather than borrowing the bar colour.
  const summary = swatchValue(swatches, view.summaryColorName) ?? theme.summary;
  const summaryDone =
    swatchValue(swatches, view.summaryDoneColorName) ??
    (view.summaryColorName ? doneFromBase(summary) : theme.summaryStrong);
  const milestone =
    swatchValue(swatches, view.milestoneColorName) ??
    (view.baseColorName ? doneFromBase(base) : theme.accentStrong);
  return { base, done, summary, summaryDone, milestone };
}

// §26/§6.3 One CSS rule pair per task that overrides the chart's colours. Tasks using the
// chart default emit nothing — they are already painted by the `--wx-gantt-*` variables.
//
// Keyed on the `data-task-id` the widget puts on every bar, because it exposes no per-task
// class of its own (its bar className is assembled from type/selection/critical and nothing
// else). The rules are scoped to one chart's own root class, so two gantts on a slide cannot
// colour each other's bars.
// **The attribute is NOT the id.** The library writes ids through its own `setID`, which
// prefixes STRING ids with a colon (`":" + id`) and leaves numbers alone — so a seeded task
// (a `crypto.randomUUID()`) is `data-task-id=":a1b2…"` while one the widget minted is
// `data-task-id="7"`. Selecting on the bare id matched nothing at all, which is why picking a
// colour for a seeded task did visibly nothing while it would have worked on an added one.
function domTaskId(id: GanttId): string {
  const encoded = typeof id === "string" ? `:${id}` : String(id);
  // Only a quote or a backslash could break out of the quoted attribute selector, and neither
  // can occur in a UUID or a number — but an id can arrive from an imported file.
  return encoded.replace(/["\\]/g, "");
}

export function ganttBarCss(
  scope: string,
  tasks: GanttTask[],
  view: GanttView,
  theme: GanttTheme,
  swatches: { name: string; value: string }[],
): string {
  const chart = resolveChartColors(view, theme, swatches);
  const rules: string[] = [];
  for (const task of tasks) {
    const base = swatchValue(swatches, task.colorName);
    const done =
      swatchValue(swatches, task.doneColorName) ??
      (base ? doneFromBase(base) : undefined);
    if (!base && !done) continue;
    const sel = `.${scope} .wx-bar[data-task-id="${domTaskId(task.id)}"]`;
    if (base) {
      rules.push(`${sel}{background-color:${base};border-color:${base}}`);
    }
    rules.push(
      `${sel} .wx-progress-percent{background-color:${done ?? chart.done}}`,
    );
  }
  return rules.join("\n");
}

export function resolveGanttTheme(styleSheet: GriddStyleSheetData): GanttTheme {
  const body = resolveRoleTypography(styleSheet, "body");
  const swatches = sheetSwatches(styleSheet);
  const named = ACCENT_NAMES.reduce<string | undefined>(
    (found, prefix) =>
      found ??
      swatches.find((c) => c.name?.trim().toLowerCase().startsWith(prefix))
        ?.value,
    undefined,
  );
  const firstColourful = swatches.find(
    (c) => c.value && !isNeutralColor(c.value),
  );
  const accent = named ?? firstColourful?.value ?? "#37a9ef";
  const ink = body.color;
  // `color-mix` rather than hex arithmetic: the accent may arrive as any CSS colour the
  // stylesheet author typed, including a named one, and this stays correct for all of them.
  return {
    fontFamily: body.fontFamily,
    fontColor: ink,
    accent,
    accentStrong: `color-mix(in srgb, ${accent} 72%, black)`,
    // Summary bars are structure, not data: a mid neutral that sits UNDER the task bars in
    // weight. The first pass put them at 78% ink, which on a sheet whose text is #333 drew a
    // row of near-black slabs.
    summary: `color-mix(in srgb, ${ink} 45%, white)`,
    summaryStrong: `color-mix(in srgb, ${ink} 65%, white)`,
    lineColor: `color-mix(in srgb, ${ink} 40%, white)`,
    borderColor: `color-mix(in srgb, ${ink} 14%, white)`,
    selectColor: `color-mix(in srgb, ${accent} 14%, white)`,
  };
}

// §26/§8.3 A new task's dates, for the panel's "Add task" button.
//
// It lands AFTER everything already scheduled rather than at today: a schedule being edited on
// a slide is usually a plan being extended, and dropping a bar in the middle of an existing
// run is a worse guess than dropping it at the end. Returns `Date` objects because this feeds
// `api.exec("add-task")`, which talks to the library's model, not to our stored one.
export function newTaskDates(
  doc: GanttDocument,
  type: "task" | "milestone",
  weekStart: WeekStart = 1,
): { start: Date; end: Date } {
  const bounds = ganttBounds(doc.tasks);
  const start = unitStart(bounds?.end ?? new Date(), doc.view.unit, weekStart);
  return {
    start,
    end: type === "milestone" ? start : addUnits(start, doc.view.unit, 2),
  };
}
